import * as React from 'react';
import { Input } from '@insolech/ui';

/**
 * PhoneInput - country-code prefix selector + national number field.
 *
 * Per Rafael's Phase 4b iteration feedback:
 *   - Default to UAE (+971)
 *   - Flag emoji is acceptable; full country names in dropdown
 *   - Phone itself is OPTIONAL - only validate if provided
 *   - Validate as E.164 if provided (basic regex client-side)
 *
 * Stores a single E.164-shaped string upstream (e.g. `+971501234567`) so
 * existing patient.phone column doesn't need any schema change.
 */

export interface CountryCode {
  iso: string;
  name: string;
  prefix: string; // e.g. "+971"
  flag: string;
}

// Curated list - UAE first because that's the dominant clinic base today.
// Extendable later; the focus is the GCC + common patient origins.
const COUNTRIES: ReadonlyArray<CountryCode> = [
  { iso: 'AE', name: 'United Arab Emirates', prefix: '+971', flag: '🇦🇪' },
  { iso: 'SA', name: 'Saudi Arabia', prefix: '+966', flag: '🇸🇦' },
  { iso: 'QA', name: 'Qatar', prefix: '+974', flag: '🇶🇦' },
  { iso: 'KW', name: 'Kuwait', prefix: '+965', flag: '🇰🇼' },
  { iso: 'BH', name: 'Bahrain', prefix: '+973', flag: '🇧🇭' },
  { iso: 'OM', name: 'Oman', prefix: '+968', flag: '🇴🇲' },
  { iso: 'EG', name: 'Egypt', prefix: '+20', flag: '🇪🇬' },
  { iso: 'JO', name: 'Jordan', prefix: '+962', flag: '🇯🇴' },
  { iso: 'LB', name: 'Lebanon', prefix: '+961', flag: '🇱🇧' },
  { iso: 'IN', name: 'India', prefix: '+91', flag: '🇮🇳' },
  { iso: 'PK', name: 'Pakistan', prefix: '+92', flag: '🇵🇰' },
  { iso: 'PH', name: 'Philippines', prefix: '+63', flag: '🇵🇭' },
  { iso: 'GB', name: 'United Kingdom', prefix: '+44', flag: '🇬🇧' },
  { iso: 'US', name: 'United States', prefix: '+1', flag: '🇺🇸' },
  { iso: 'FR', name: 'France', prefix: '+33', flag: '🇫🇷' },
  { iso: 'DE', name: 'Germany', prefix: '+49', flag: '🇩🇪' },
  { iso: 'ES', name: 'Spain', prefix: '+34', flag: '🇪🇸' },
  { iso: 'IT', name: 'Italy', prefix: '+39', flag: '🇮🇹' },
];

const DEFAULT_PREFIX = '+971';

export interface PhoneInputProps {
  /** Full E.164 string (or empty string for "no value"). */
  value: string;
  onChange: (next: string) => void;
  /** Validation error message - surfaced inline by the form field. */
  error?: string | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
}

/**
 * Parses an E.164-shaped phone into the picked country prefix + national digits.
 * Returns the longest-matching country prefix.
 */
export function parsePhone(value: string): { prefix: string; national: string } {
  if (!value) return { prefix: DEFAULT_PREFIX, national: '' };
  const match = COUNTRIES.slice()
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find((c) => value.startsWith(c.prefix));
  if (match) {
    return { prefix: match.prefix, national: value.slice(match.prefix.length) };
  }
  return { prefix: DEFAULT_PREFIX, national: value.replace(/^\+/, '') };
}

/**
 * Validates an E.164 phone string. Returns null if valid (or empty), or an
 * error message string. Phone is optional; empty string is valid.
 */
export function validatePhone(value: string): string | null {
  if (!value || value === '') return null;
  // Loose E.164: + then 8..15 digits total
  if (!/^\+\d{8,15}$/.test(value)) {
    return 'Phone must be in international format (e.g. +971501234567)';
  }
  return null;
}

export function PhoneInput({ value, onChange, error, disabled, id }: PhoneInputProps) {
  const parsed = React.useMemo(() => parsePhone(value), [value]);

  const setPrefix = (newPrefix: string) => {
    onChange(`${newPrefix}${parsed.national}`);
  };
  const setNational = (raw: string) => {
    // strip everything but digits and spaces
    const digits = raw.replace(/[^\d\s]/g, '').replace(/\s+/g, '');
    onChange(digits ? `${parsed.prefix}${digits}` : '');
  };

  return (
    <div className="flex gap-2">
      {/* Mobile (<640px): the native select sizes itself to the LONGEST
          option ("🇦🇪 +971 United Arab Emirates" ≈ 250px) and used to force
          the row past the viewport. max-sm clamps it to flag+prefix width
          (the opened dropdown still shows full country names); 16px text so
          iOS doesn't zoom. min-w-0 lets the tel input actually shrink.
          Desktop unchanged. */}
      <select
        aria-label="Country code"
        value={parsed.prefix}
        onChange={(e) => setPrefix(e.target.value)}
        disabled={disabled}
        className="rounded-md border border-border-default bg-surface-elevated px-2 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 max-sm:w-28"
      >
        {COUNTRIES.map((c) => (
          <option key={c.iso} value={c.prefix}>
            {c.flag} {c.prefix} {c.name}
          </option>
        ))}
      </select>
      <Input
        id={id}
        type="tel"
        value={parsed.national}
        onChange={(e) => setNational(e.target.value)}
        placeholder="50 123 4567"
        disabled={disabled}
        className="flex-1 max-sm:min-w-0"
        aria-invalid={!!error || undefined}
      />
    </div>
  );
}
